import { ChevronRight, ListChecks } from "lucide-react";
import { cn } from "@/lib/utils";
import { KIT } from "@/constants/testIds";

/** One option inside a "choose any one" meal. An option with a recipe opens it; a plan-authored
 *  option with no recipe (a snack choice) is a label, so it is not an actionable button. */
const Option = ({ slot, option, onOpenRecipe }) => {
  const openable = !!option.recipe_id;
  const Tag = openable ? "button" : "div";
  return (
    <Tag
      data-testid={KIT.mealOption(slot, option.title)}
      onClick={openable ? () => onOpenRecipe?.({ ...option, slot }) : undefined}
      className={cn(
        "group flex h-full w-full items-center gap-3.5 overflow-hidden rounded-[16px] border border-brand-teal/12 bg-white p-2.5 pr-3.5 text-left",
        openable &&
          "ps-tap transition-all duration-300 hover:-translate-y-[1px] hover:border-brand-orange/50 hover:shadow-[0_10px_22px_-14px_rgba(15,46,44,0.35)]"
      )}
    >
      {/* The 1:1 photo appears as soon as the recipe is mapped and synced from WordPress. */}
      {option.image ? (
        <span className="ps-photo block aspect-square w-[68px] shrink-0 self-start rounded-[12px] sm:w-[76px]">
          <img
            src={option.image}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.07]"
          />
        </span>
      ) : (
        <span
          className="grid aspect-square w-[68px] shrink-0 self-start place-items-center rounded-[12px] font-display text-lg font-bold text-white/85 sm:w-[76px]"
          style={{ backgroundColor: "#17403B" }}
        >
          {option.title?.[0]}
        </span>
      )}

      <span
        className={cn(
          "min-w-0 flex-1 font-display text-[14.5px] font-bold leading-[1.3] text-brand-teal sm:text-[15px]",
          openable && "transition-colors group-hover:text-brand-orangeInk"
        )}
      >
        {option.title}
      </span>
      {openable && (
        <ChevronRight className="h-4 w-4 shrink-0 text-brand-teal/30 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-brand-orangeInk" />
      )}
    </Tag>
  );
};

/** A "choose any one" meal, presented as part of the plan rather than a generic list.
 *  Fully content-driven: any program gets this by authoring `choose` + `options`, each option
 *  carrying its own recipe key and therefore its own 1:1 WPRM photo. */
export const ChoiceMeal = ({ slot, choose, options, onOpenRecipe }) => (
  <div data-testid={KIT.mealChoice(slot)} className="mt-4">
    <div className="flex flex-wrap items-center gap-2.5">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-orange px-3 py-[5px] text-[11px] font-bold uppercase tracking-[0.05em] text-white">
        <ListChecks className="h-3 w-3" />
        {choose || "Choose any one"}
      </span>
      <span
        data-testid={KIT.mealChoiceCount(slot)}
        className="text-[11px] font-bold uppercase tracking-[0.08em] text-brand-teal/45"
      >
        {options.length} options
      </span>
    </div>

    <ul className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
      {options.map((option) => (
        <li key={option.title}>
          <Option slot={slot} option={option} onOpenRecipe={onOpenRecipe} />
        </li>
      ))}
    </ul>
  </div>
);
